import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const copy = {
  en: {
    nav: {
      application: "Application",
      roadmap: "Roadmap",
      about: "About us",
      contact: "Contact us",
      login: "Client login",
    },
    hero: {
      eyebrow: "Managed monitoring and reporting for PV assets",
      title: "PV-REPORT",
      subtitle: "PV power plant monitoring, reporting, and O&M intelligence in one operational platform.",
      body:
        "PV-REPORT helps operators and asset owners monitor live plant performance, track inverter and tracker behavior, generate structured reports, and turn technical deviations into actionable maintenance work.",
      primary: "Request a Demo",
      secondary: "Explore the platform",
      status: "Production monitoring, reporting, and O&M workflows for utility-scale PV assets.",
    },
    capabilitiesTitle: "Operational Capabilities",
    capabilitiesIntro:
      "PV-REPORT is built around real plant telemetry, structured reporting, and workflows that support daily technical decisions.",
    capabilities: [
      {
        title: "Live monitoring",
        text:
          "The site-local pvapp-gateway acquires telemetry on site, keeps an auditable local record, and uploads accepted data securely to PV-REPORT over HTTPS with mTLS.",
      },
      {
        title: "High-definition telemetry",
        text:
          "PV-REPORT is designed around 1-minute operational telemetry where the site hardware and gateway configuration support it, preserving short inverter, tracker, curtailment, and weather-driven events.",
      },
      {
        title: "Performance analytics",
        text:
          "Expected production, PR-style KPIs, inverter-level deviation analysis, availability, and daily summaries help operators focus on real performance losses.",
      },
      {
        title: "Tracker intelligence",
        text:
          "Tracker status and daily tracker reports highlight angle deviation, communication quality, wind/stow behavior, battery status, and alarms where tracker telemetry is available.",
      },
      {
        title: "Reporting",
        text:
          "Generate daily performance reports, tracker reports, IEC PR evaluations, inverter availability views, and export-ready operational summaries.",
      },
      {
        title: "O&M workflow",
        text:
          "Maintenance tickets connect diagnostics, operator notes, attachments, status changes, and plant follow-up in one structured workflow.",
      },
      {
        title: "Forecasting roadmap",
        text:
          "A 48-hour ML-assisted energy yield forecast is coming soon, built on weather forecasts, plant metadata, solar geometry, historical performance, and tracker/wind-stow features.",
      },
      {
        title: "Aggregator communication",
        text:
          "The gateway supports energy trader and aggregator communication. The ALTEO API adapter is already implemented, and other aggregator adapters can be developed on demand.",
      },
    ],
    application: {
      label: "Application",
      title: "One platform for plant visibility",
      text:
        "PV-REPORT brings site, transformer, array, inverter, tracker, weather, and maintenance information into a single operational interface. Operators can move from portfolio-level status to plant topology, inverter performance, tracker status, reports, and tickets without leaving the application.",
      bullets: [
        "Multi-site dashboard",
        "Site hierarchy and transformer-level views",
        "Inverter performance and availability",
        "Tracker monitoring and daily tracker reports",
        "Maintenance ticket workflow",
        "Role-based access for organizations and users",
      ],
    },
    reports: {
      label: "Reporting",
      title: "Reports built from operational data",
      text:
        "PV-REPORT stores normalized telemetry and calculated KPIs so reports can be regenerated, reviewed, and compared. The reporting layer supports daily production reports, inverter availability, tracker behavior, and IEC PR workflows with section-level evaluation and manual exclusions.",
    },
    gateway: {
      label: "Gateway",
      title: "Site-local acquisition. Secure central visibility.",
      text:
        "PV-REPORT is designed around the pvapp-gateway: a site-local gateway that acquires telemetry inside the plant network, keeps local audit records, and uploads validated telemetry to the central platform through HTTPS with mutual TLS.",
      extra:
        "Because data is collected and buffered on site, temporary internet or communication line outages do not have to create gaps in the operational record. When connectivity returns, the gateway can continue uploading completed acquisition cycles with batch-level identity, retry state, and acceptance tracking.",
    },
    roadmap: {
      label: "Roadmap",
      title: "Coming soon: forecasting and O&M logbook",
      text:
        "The next implementation steps are the 48-hour energy yield forecasting module and a full O&M logbook. Forecasting will combine weather forecasts, solar geometry, tracker behavior, historical measured production, clear-sky modelling, and ML-based cloud attenuation prediction.",
      extra:
        "The O&M logbook expands the existing ticket workflow into structured operational record keeping for interventions, notes, attachments, status changes, and closure information.",
    },
    aggregator: {
      label: "Integration",
      title: "Ready for energy trader communication",
      text:
        "The pvapp-gateway can act as a controlled communication bridge for energy trader or aggregator requirements. The current implementation includes ALTEO API integration, configurable payload signals, setpoint handling, and a separated write-agent architecture for controlled plant setpoint application.",
    },
    service: {
      label: "Managed service",
      title: "No local software stack to maintain",
      text:
        "PV-REPORT is delivered as a managed web platform. Customers access dashboards, reports, tickets, and configuration tools through the browser, without maintaining local application servers, database servers, reporting servers, or desktop software.",
    },
    about: {
      title: "About PV-REPORT",
      text:
        "PV-REPORT is developed by T-NRG Kft. for practical PV power plant operation: monitoring live telemetry, understanding performance deviations, generating reports, and keeping maintenance activity connected to plant data.",
    },
    contact: {
      title: "Contact us",
      text:
        "Interested in PV-REPORT for your PV plant or portfolio? Contact us to discuss monitoring, reporting, gateway integration, tracker supervision, aggregator communication, and O&M workflow requirements.",
      email: "pvreport@t-nrg.com",
      phone: "+36 30 558 9688",
      address: "T-NRG Kft., Dózsa György utca 12/A, 5000 Szolnok, Hungary",
    },
    footer: {
      imprint: "Imprint",
      privacy: "Privacy Policy",
      cookies: "Cookie Policy",
    },
  },
  hu: {
    nav: {
      application: "Alkalmazás",
      roadmap: "Ütemterv",
      about: "Rólunk",
      contact: "Kapcsolat",
      login: "Ügyfél belépés",
    },
    hero: {
      eyebrow: "Menedzselt monitoring és riportolás naperőművi eszközökhöz",
      title: "PV-REPORT",
      subtitle: "Naperőművi monitoring, riportolás és O&M intelligencia egy operatív platformon.",
      body:
        "A PV-REPORT segíti az üzemeltetőket és tulajdonosokat az élő termelés követésében, az inverter- és tracker-működés elemzésében, a strukturált riportok készítésében és a műszaki eltérések karbantartási feladattá alakításában.",
      primary: "Demó kérése",
      secondary: "Platform áttekintése",
      status: "Termelésmonitoring, riportolás és O&M munkafolyamatok ipari méretű naperőművi eszközökhöz.",
    },
    capabilitiesTitle: "Operatív képességek",
    capabilitiesIntro:
      "A PV-REPORT valós naperőművi telemetriára, strukturált riportolásra és napi műszaki döntéseket támogató munkafolyamatokra épül.",
    capabilities: [
      {
        title: "Élő monitoring",
        text:
          "A helyszíni pvapp-gateway a naperőmű hálózatán belül gyűjti a telemetriát, auditálható helyi rekordot vezet, majd az elfogadott adatokat HTTPS és mTLS kapcsolaton keresztül tölti fel a PV-REPORT rendszerbe.",
      },
      {
        title: "Nagy felbontású telemetria",
        text:
          "A PV-REPORT 1 perces operatív telemetriára készült ott, ahol a helyszíni hardver és gateway konfiguráció ezt támogatja, így a rövid inverter-, tracker-, leszabályozási és időjárási események sem vesznek el.",
      },
      {
        title: "Teljesítmény-analitika",
        text:
          "Várható termelés, PR jellegű KPI-ok, inverter szintű eltérésvizsgálat, rendelkezésre állás és napi összesítők segítik a valós teljesítményveszteségek azonosítását.",
      },
      {
        title: "Tracker intelligencia",
        text:
          "A tracker státusz és napi tracker riportok láthatóvá teszik a szögeltérést, kommunikációs minőséget, szél/stow viselkedést, akkuállapotot és riasztásokat, ahol tracker telemetria elérhető.",
      },
      {
        title: "Riportolás",
        text:
          "Napi termelési riportok, tracker riportok, IEC PR értékelések, inverter rendelkezésre állási nézetek és exportálható operatív összesítők készíthetők.",
      },
      {
        title: "O&M munkafolyamat",
        text:
          "A karbantartási hibajegyek összekötik a diagnosztikát, üzemeltetői megjegyzéseket, csatolmányokat, állapotváltásokat és helyszíni utánkövetést.",
      },
      {
        title: "Forecasting ütemterv",
        text:
          "A 48 órás, ML-támogatott energiatermelési előrejelzés hamarosan érkezik, időjárás-előrejelzésekre, naperőművi metaadatokra, napgeometriára, historikus teljesítményre és tracker/wind-stow jellemzőkre építve.",
      },
      {
        title: "Aggregator kommunikáció",
        text:
          "A gateway támogatja az energiakereskedői és aggregator kommunikációt. Az ALTEO API adapter már implementált, további aggregator adapterek igény szerint fejleszthetők.",
      },
    ],
    application: {
      label: "Alkalmazás",
      title: "Egy platform a naperőművi átláthatósághoz",
      text:
        "A PV-REPORT egy operatív felületen kapcsolja össze a telephely, transzformátor, mezőszerkezet, inverter, tracker, meteorológiai és karbantartási információkat. Az üzemeltető a portfóliótól a topológiáig, inverter teljesítményig, tracker státuszig, riportokig és hibajegyekig egy alkalmazáson belül jut el.",
      bullets: [
        "Több telephelyes dashboard",
        "Telephely-hierarchia és transzformátor szintű nézetek",
        "Inverter teljesítmény és rendelkezésre állás",
        "Tracker monitoring és napi tracker riportok",
        "Karbantartási hibajegy munkafolyamat",
        "Szervezeti és felhasználói jogosultságkezelés",
      ],
    },
    reports: {
      label: "Riportolás",
      title: "Operatív adatokból épülő riportok",
      text:
        "A PV-REPORT normalizált telemetriát és számított KPI-okat tárol, így a riportok újragenerálhatók, ellenőrizhetők és összehasonlíthatók. A riporting réteg napi termelési riportokat, inverter rendelkezésre állást, tracker viselkedést és IEC PR munkafolyamatokat támogat.",
    },
    gateway: {
      label: "Gateway",
      title: "Helyszíni adatgyűjtés. Biztonságos központi láthatóság.",
      text:
        "A PV-REPORT a pvapp-gateway architektúrára épül: a helyszíni gateway a naperőmű hálózatán belül gyűjti az adatokat, helyi audit rekordokat vezet, és a validált telemetriát HTTPS és kölcsönös TLS kapcsolaton tölti fel a központi platformra.",
      extra:
        "Mivel az adatgyűjtés és pufferelés helyben történik, az ideiglenes internet- vagy kommunikációs kiesések nem feltétlenül okoznak hiányokat az operatív rekordban. A kapcsolat visszaállása után a gateway folytatni tudja a lezárt adatgyűjtési ciklusok feltöltését.",
    },
    roadmap: {
      label: "Ütemterv",
      title: "Hamarosan: forecasting és Karbantartási Napló",
      text:
        "A következő fejlesztési lépések a 48 órás energiatermelési előrejelző modul és a teljes Karbantartási Napló. A forecasting időjárás-előrejelzéseket, napgeometriát, tracker viselkedést, historikus mért termelést, clear-sky modellezést és ML-alapú felhőattenuáció becslést fog összekapcsolni.",
      extra:
        "A Karbantartási Napló a meglévő hibajegy munkafolyamatot bővíti strukturált üzemeltetési nyilvántartássá: beavatkozások, megjegyzések, csatolmányok, állapotváltások és lezárási információk kezelésére.",
    },
    aggregator: {
      label: "Integráció",
      title: "Felkészítve energiakereskedői kommunikációra",
      text:
        "A pvapp-gateway kontrollált kommunikációs hídként működhet energiakereskedői vagy aggregator igényekhez. A jelenlegi implementáció ALTEO API integrációt, konfigurálható payload jeleket, setpoint kezelést és elkülönített write-agent architektúrát tartalmaz.",
    },
    service: {
      label: "Menedzselt szolgáltatás",
      title: "Nincs helyi szoftverstacket fenntartani",
      text:
        "A PV-REPORT menedzselt webplatformként érhető el. Az ügyfelek böngészőből használják a dashboardokat, riportokat, hibajegyeket és konfigurációs eszközöket, helyi alkalmazásszerver, adatbázis-szerver, riport szerver vagy asztali szoftver üzemeltetése nélkül.",
    },
    about: {
      title: "A PV-REPORT-ról",
      text:
        "A PV-REPORT-ot a T-NRG Kft. fejleszti gyakorlati naperőművi üzemeltetéshez: élő telemetria követéséhez, teljesítmény-eltérések megértéséhez, riportok generálásához és a karbantartási aktivitások naperőművi adatokhoz kapcsolásához.",
    },
    contact: {
      title: "Kapcsolat",
      text:
        "Érdekli a PV-REPORT a naperőműve vagy portfóliója számára? Vegye fel velünk a kapcsolatot monitoring, riportolás, gateway integráció, tracker felügyelet, aggregator kommunikáció és O&M munkafolyamatok egyeztetéséhez.",
      email: "pvreport@t-nrg.com",
      phone: "+36 30 558 9688",
      address: "T-NRG Kft., Dózsa György utca 12/A, 5000 Szolnok, Magyarország",
    },
    footer: {
      imprint: "Impresszum",
      privacy: "Adatkezelési tájékoztató",
      cookies: "Sütikezelési tájékoztató",
    },
  },
};

const images = {
  hero: "/landing/hero-pv-plant.jpg",
  dashboard: "/landing/screenshot-dashboard.jpg",
  hierarchy: "/landing/screenshot-hierarchy.jpg",
  reports: "/landing/screenshot-reports.jpg",
  tracker: "/landing/screenshot-tracker-status.jpg",
  gateway: "/landing/gateway.jpg",
  field: "/landing/field_image_tracker.jpg",
  trader: "/landing/energy_trader.jpg",
};

function SectionLabel({ children }) {
  return <div className="text-sm font-semibold uppercase tracking-wide text-emerald-700">{children}</div>;
}

function LanguageToggle({ language, setLanguage }) {
  return (
    <div className="inline-flex rounded-md border border-slate-300 bg-white p-1 text-sm" aria-label="Language">
      {["en", "hu"].map((lang) => (
        <button
          key={lang}
          type="button"
          className={`rounded px-3 py-1 font-medium focus:outline-none focus:ring-2 focus:ring-emerald-600 ${
            language === lang ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
          }`}
          onClick={() => setLanguage(lang)}
          data-analytics-event={`language_${lang}`}
        >
          {lang.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

function LandingAnalytics() {
  useEffect(() => {
    const scriptSrc = process.env.REACT_APP_UMAMI_SCRIPT_SRC;
    const websiteId = process.env.REACT_APP_UMAMI_WEBSITE_ID;

    if (!scriptSrc || !websiteId || typeof document === "undefined") return undefined;

    let script = document.querySelector(`script[data-pvreport-analytics="landing"]`);
    let cancelled = false;

    const trackPage = () => {
      if (!cancelled && window.umami) {
        window.umami.track();
      }
    };

    if (!script) {
      script = document.createElement("script");
      script.defer = true;
      script.src = scriptSrc;
      script.dataset.websiteId = websiteId;
      script.dataset.autoTrack = "false";
      script.dataset.pvreportAnalytics = "landing";
      script.addEventListener("load", trackPage, { once: true });
      document.head.appendChild(script);
    } else {
      trackPage();
    }

    const handleClick = (event) => {
      const target = event.target.closest("[data-analytics-event]");
      const eventName = target?.dataset?.analyticsEvent;
      if (eventName && window.umami) {
        window.umami.track(eventName);
      }
    };

    document.addEventListener("click", handleClick);
    return () => {
      cancelled = true;
      document.removeEventListener("click", handleClick);
    };
  }, []);

  return null;
}

function Header({ t, language, setLanguage }) {
  return (
    <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
        <a href="#top" className="flex items-center gap-3 focus:outline-none focus:ring-2 focus:ring-emerald-600">
          <img src="/pvapplogo.png" alt="PV-REPORT logo" className="h-9 w-9 rounded" />
          <span className="text-lg font-bold tracking-normal text-slate-950">PV-REPORT</span>
        </a>
        <nav className="hidden items-center gap-6 text-sm font-medium text-slate-700 lg:flex">
          <a className="hover:text-slate-950" href="#application">
            {t.nav.application}
          </a>
          <a className="hover:text-slate-950" href="#roadmap">
            {t.nav.roadmap}
          </a>
          <a className="hover:text-slate-950" href="#about">
            {t.nav.about}
          </a>
          <a className="hover:text-slate-950" href="#contact">
            {t.nav.contact}
          </a>
        </nav>
        <div className="flex items-center gap-2">
          <LanguageToggle language={language} setLanguage={setLanguage} />
          <Link
            to="/login"
            className="hidden rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-600 sm:inline-flex"
            data-analytics-event="client_login_header"
          >
            {t.nav.login}
          </Link>
        </div>
      </div>
    </header>
  );
}

function Hero({ t }) {
  return (
    <section id="top" className="relative overflow-hidden bg-slate-950 text-white">
      <img src={images.hero} alt="Utility-scale photovoltaic plant" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-slate-950/75" />
      <div className="relative mx-auto grid min-h-[620px] max-w-7xl items-center gap-10 px-4 py-20 sm:px-6 lg:grid-cols-[1fr_460px] lg:px-8">
        <div className="max-w-3xl">
          <div className="mb-5 text-sm font-semibold uppercase tracking-wide text-emerald-300">{t.hero.eyebrow}</div>
          <h1 className="text-5xl font-bold tracking-normal sm:text-6xl">{t.hero.title}</h1>
          <p className="mt-6 max-w-2xl text-2xl font-semibold leading-snug text-white">{t.hero.subtitle}</p>
          <p className="mt-5 max-w-2xl text-lg leading-8 text-slate-200">{t.hero.body}</p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a
              href="#contact"
              className="inline-flex justify-center rounded-md bg-emerald-500 px-5 py-3 text-sm font-bold text-slate-950 hover:bg-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200"
              data-analytics-event="request_demo_hero"
            >
              {t.hero.primary}
            </a>
            <a
              href="#application"
              className="inline-flex justify-center rounded-md border border-white/40 px-5 py-3 text-sm font-bold text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white"
              data-analytics-event="explore_platform_hero"
            >
              {t.hero.secondary}
            </a>
          </div>
          <p className="mt-6 text-sm font-medium text-slate-300">{t.hero.status}</p>
        </div>
        <div className="hidden lg:block">
          <div className="overflow-hidden rounded-lg border border-white/20 bg-white/10 shadow-2xl">
            <img src={images.dashboard} alt="PV-REPORT dashboard screenshot" className="h-full w-full object-cover" />
          </div>
        </div>
      </div>
    </section>
  );
}

function Capabilities({ t }) {
  return (
    <section className="bg-slate-50 py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <h2 className="text-3xl font-bold tracking-normal text-slate-950">{t.capabilitiesTitle}</h2>
          <p className="mt-4 text-lg leading-8 text-slate-600">{t.capabilitiesIntro}</p>
        </div>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {t.capabilities.map((item) => (
            <article key={item.title} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="text-base font-bold text-slate-950">{item.title}</h3>
              <p className="mt-3 text-sm leading-6 text-slate-600">{item.text}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function ImageTextSection({ id, label, title, text, extra, bullets, image, imageAlt, reverse = false }) {
  return (
    <section id={id} className="bg-white py-20">
      <div
        className={`mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:px-8 ${
          reverse ? "lg:[&>*:first-child]:order-2" : ""
        }`}
      >
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-100 shadow-sm">
          <img src={image} alt={imageAlt} className="aspect-[16/10] h-full w-full object-cover" />
        </div>
        <div>
          <SectionLabel>{label}</SectionLabel>
          <h2 className="mt-3 text-3xl font-bold tracking-normal text-slate-950">{title}</h2>
          <p className="mt-5 text-lg leading-8 text-slate-600">{text}</p>
          {extra && <p className="mt-4 text-lg leading-8 text-slate-600">{extra}</p>}
          {bullets && (
            <ul className="mt-6 grid gap-3 text-sm font-medium text-slate-700 sm:grid-cols-2">
              {bullets.map((bullet) => (
                <li key={bullet} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                  {bullet}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

function Contact({ t }) {
  return (
    <section id="contact" className="bg-slate-950 py-20 text-white">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[1fr_420px] lg:px-8">
        <div>
          <h2 className="text-3xl font-bold tracking-normal">{t.contact.title}</h2>
          <p className="mt-5 max-w-3xl text-lg leading-8 text-slate-200">{t.contact.text}</p>
        </div>
        <address className="not-italic">
          <div className="rounded-lg border border-white/15 bg-white/10 p-6">
            <dl className="space-y-4 text-sm">
              <div>
                <dt className="font-semibold text-slate-300">Email</dt>
                <dd className="mt-1">
                  <a className="font-bold text-emerald-300 hover:text-emerald-200" href={`mailto:${t.contact.email}`}>
                    {t.contact.email}
                  </a>
                </dd>
              </div>
              <div>
                <dt className="font-semibold text-slate-300">Phone</dt>
                <dd className="mt-1 text-white">{t.contact.phone}</dd>
              </div>
              <div>
                <dt className="font-semibold text-slate-300">Address</dt>
                <dd className="mt-1 text-white">{t.contact.address}</dd>
              </div>
            </dl>
          </div>
        </address>
      </div>
    </section>
  );
}

function Footer({ t }) {
  return (
    <footer className="border-t border-slate-200 bg-white py-8">
      <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 text-sm text-slate-600 sm:px-6 md:flex-row md:items-center md:justify-between lg:px-8">
        <div>© {new Date().getFullYear()} T-NRG Kft. PV-REPORT</div>
        <div className="flex flex-wrap gap-4">
          <Link className="hover:text-slate-950" to="/imprint">
            {t.footer.imprint}
          </Link>
          <Link className="hover:text-slate-950" to="/privacy">
            {t.footer.privacy}
          </Link>
          <Link className="hover:text-slate-950" to="/cookies">
            {t.footer.cookies}
          </Link>
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  const [language, setLanguage] = useState("en");
  const t = copy[language];

  return (
    <div className="min-h-screen bg-white text-slate-950">
      <LandingAnalytics />
      <Header t={t} language={language} setLanguage={setLanguage} />
      <main>
        <Hero t={t} />
        <Capabilities t={t} />
        <ImageTextSection
          id="application"
          label={t.application.label}
          title={t.application.title}
          text={t.application.text}
          bullets={t.application.bullets}
          image={images.hierarchy}
          imageAlt="PV-REPORT site hierarchy screenshot"
        />
        <ImageTextSection
          label={t.reports.label}
          title={t.reports.title}
          text={t.reports.text}
          image={images.reports}
          imageAlt="PV-REPORT reports screenshot"
          reverse
        />
        <ImageTextSection
          label={t.gateway.label}
          title={t.gateway.title}
          text={t.gateway.text}
          extra={t.gateway.extra}
          image={images.gateway}
          imageAlt="Site-local PV gateway equipment"
        />
        <ImageTextSection
          id="roadmap"
          label={t.roadmap.label}
          title={t.roadmap.title}
          text={t.roadmap.text}
          extra={t.roadmap.extra}
          image={images.tracker}
          imageAlt="PV-REPORT tracker status screenshot"
          reverse
        />
        <ImageTextSection
          label={t.aggregator.label}
          title={t.aggregator.title}
          text={t.aggregator.text}
          image={images.trader}
          imageAlt="Energy trader communication context"
        />
        <ImageTextSection
          label={t.service.label}
          title={t.service.title}
          text={t.service.text}
          image={images.field}
          imageAlt="Photovoltaic tracker rows in the field"
          reverse
        />
        <section id="about" className="bg-slate-50 py-20">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold tracking-normal text-slate-950">{t.about.title}</h2>
            <p className="mt-5 text-lg leading-8 text-slate-600">{t.about.text}</p>
          </div>
        </section>
        <Contact t={t} />
      </main>
      <Footer t={t} />
    </div>
  );
}
